/** The account screen's tabs, by route. */
export type AccountOptionTab = 'profile' | 'settings';

/**
 * One option on the account screen that search can find. Each panel defines its options beside the controls they
 * name, and renders each control with its label and `accountOptionTarget(option)`, so the index can't drift from the
 * screen.
 */
export interface AccountOption {
  /** Unique across the account screen; marks the option's element for focus. */
  id: string;
  tab: AccountOptionTab;
  /** The title of the section the option sits in. */
  section: string;
  /** The option's visible label. */
  label: string;
  /** Other words a player might search for it by, e.g. "dark" for Theme. */
  synonyms: readonly string[];
  /**
   * For an option that needs an account: completes "Sign in to …" for a guest whose search matches it, e.g. "change
   * your keyboard layout". Without it a guest's match is ignored.
   */
  signInTo?: string;
}

/** An option as one player sees it: `usable` when they can act on it, false when it needs an account they don't have. */
export interface AccountSearchEntry {
  option: AccountOption;
  usable: boolean;
}

export const ACCOUNT_OPTION_ATTRIBUTE = 'data-account-option';

/** Set on an option's element while search has just taken the player to it, to highlight it. */
export const ACCOUNT_OPTION_FOUND_ATTRIBUTE = 'data-account-option-found';

/** Marks the element that holds an option, for search to focus. Spread onto the control or its wrapper. */
export const accountOptionTarget = (option: AccountOption) => ({ [ACCOUNT_OPTION_ATTRIBUTE]: option.id });

/** The option's own control first: a radio group's chosen radio, then the first enabled control or link inside. */
const FOCUSABLE = [
  'input[type="radio"]:checked:not(:disabled)',
  'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled)',
];

/** The element marked as holding an option, or undefined when it isn't on screen. */
export const findAccountOption = (root: ParentNode, id: string): HTMLElement | undefined =>
  root.querySelector<HTMLElement>(`[${ACCOUNT_OPTION_ATTRIBUTE}="${CSS.escape(id)}"]`) ?? undefined;

/**
 * The element to focus for an option under `root`: the target itself when it's a control, else the first control
 * inside it, else the target when it takes focus (`tabIndex={-1}`). Undefined when it isn't on screen.
 */
export const findAccountOptionTarget = (root: ParentNode, id: string): HTMLElement | undefined => {
  const target = findAccountOption(root, id);
  if (!target) return undefined;
  if (FOCUSABLE.some((selector) => target.matches(selector))) return target;
  for (const selector of FOCUSABLE) {
    const control = target.querySelector<HTMLElement>(selector);
    if (control) return control;
  }
  return target.hasAttribute('tabindex') ? target : undefined;
};
